<template>
    <div class="scanner-modal">
        <div class="scanner-content">
            <p v-if="title" class="fw-semibold mb-2">{{ title }}</p>

            <!-- `constraints` is a computed, never an inline literal: the library
                 deep-watches it and restarts the stream when it changes, and that
                 restart is exactly how switchToNextLens() swaps cameras. -->
            <qrcode-stream v-if="!errorMessage" ref="stream" :formats="formats" :constraints="cameraConstraints"
                :track="noopTrack" @detect="onDetect" @camera-on="onCameraOn" @error="onCameraError" />

            <p v-else class="text-danger my-3">{{ errorMessage }}</p>

            <slot name="footer"></slot>

            <button type="button" class="btn btn-primary mt-3" @click="$emit('close')">
                {{ $t('common.close') }}
            </button>
        </div>
    </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import {
    buildConstraints,
    focusSupport,
    listRearCameras,
    loadPreferredDeviceId,
    savePreferredDeviceId,
    clearPreferredDeviceId,
    mapCameraError,
} from '@/utils/camera';

// Each lens swap is a full stop + getUserMedia + a 500ms settle inside the
// library, so roughly 1.5s of black preview. A phone that reports every one of
// its lenses as fixed-focus would otherwise be walked end to end, so the search
// is bounded: three covers the usual main/ultra-wide/macro trio, and the cost
// of giving up early is a soft preview rather than a broken one.
const MAX_LENS_ATTEMPTS = 3;

export default {
    name: 'BarcodeScanner',
    components: { QrcodeStream },
    props: {
        formats: {
            type: Array,
            default: () => ['qr_code'],
        },
        title: {
            type: String,
            default: '',
        },
    },
    emits: ['decode', 'close'],
    data() {
        return {
            // Seeded from localStorage so a phone that needed a specific lens
            // last time opens straight onto it, with no probing at all.
            activeDeviceId: loadPreferredDeviceId(),
            triedDeviceIds: [],
            lensProbeDone: false,
            deviceIdRetried: false,
            scanProcessing: false,
            // A value the parent has already looked at and could not use. The
            // same label stays in frame after a failed lookup and would other-
            // wise be re-reported every 40ms.
            rejectedValue: null,
            errorMessage: '',
        };
    },
    computed: {
        cameraConstraints() {
            return buildConstraints(this.activeDeviceId);
        },
    },
    methods: {
        // vue-qrcode-reader sets its scan interval to
        // `track === undefined ? 500 : 40` ms, so passing any function at all
        // buys 12x more decode attempts per second. On a phone that only drifts
        // into focus intermittently, four attempts over two seconds is the
        // difference between reading the code and not. This one draws nothing --
        // the speed is the entire point, the highlight box is not wanted here.
        // Must stay a stable method reference so the deep watcher sees no change.
        noopTrack() { },

        // The library renders a single <video> inside its root element and
        // exposes no ref to the track. Resolve it from the DOM on every use and
        // never cache it: the stream is torn down and rebuilt on any constraints
        // change, which would leave a cached track dead.
        videoTrack() {
            const video = this.$refs.stream?.$el?.querySelector('video');
            return video?.srcObject?.getVideoTracks?.()[0] ?? null;
        },

        async onCameraOn(capabilities) {
            const caps = capabilities ?? {};
            const track = this.videoTrack();
            const settings = track?.getSettings?.() ?? {};

            // Permanent and debug-level. When the next "it's blurry on my phone"
            // report arrives, this one line says whether it is the lens (no
            // focusMode), the resolution, or neither -- in a screenshot, without
            // needing the device in hand.
            console.debug('[scanner] camera on', {
                label: track?.label,
                deviceId: settings.deviceId,
                resolution: `${settings.width}x${settings.height}`,
                focusMode: caps.focusMode,
                zoom: caps.zoom,
                attempt: this.triedDeviceIds.length + 1,
            });

            await this.normaliseZoom(caps, track);

            if (this.lensProbeDone) return;

            const focus = focusSupport(caps);

            if (focus === 'autofocus') {
                // This lens can focus -- we are done. On a Pixel this is the
                // first camera, so nothing below ever runs and the behaviour is
                // exactly what it was before.
                savePreferredDeviceId(settings.deviceId);
                this.lensProbeDone = true;
                return;
            }

            if (focus === 'unknown') {
                // The browser does not report focus capabilities (Safari,
                // Firefox). We have no basis to call this lens bad, and swapping
                // blind would cost seconds of black preview to fix nothing.
                this.lensProbeDone = true;
                return;
            }

            await this.switchToNextLens(settings.deviceId);
        },

        // A lens reporting zoom below 1x is an ultra-wide. Pulling it to 1x
        // crops to a normal field of view, which puts more pixels across the
        // barcode, and on several Android devices is what makes the OS hand over
        // the main camera instead. applyConstraints on the live track, so no
        // stream restart and nothing flickers.
        async normaliseZoom(caps, track) {
            if (!track || !caps.zoom || typeof caps.zoom.min !== 'number') return;
            if (caps.zoom.min >= 1 || caps.zoom.max < 1) return;

            try {
                await track.applyConstraints({ advanced: [{ zoom: 1 }] });
            } catch (e) {
                console.debug('[scanner] zoom not applied:', e);
            }
        },

        async switchToNextLens(currentDeviceId) {
            if (currentDeviceId && !this.triedDeviceIds.includes(currentDeviceId)) {
                this.triedDeviceIds.push(currentDeviceId);
            }

            if (this.triedDeviceIds.length >= MAX_LENS_ATTEMPTS) {
                // Out of budget. Keep whatever is streaming rather than leaving
                // the user with nothing -- a soft picture still beats a black
                // box, and the photo they are scanning may yet be legible.
                console.debug('[scanner] no autofocus-capable lens found, keeping current');
                this.lensProbeDone = true;
                return;
            }

            const cameras = await listRearCameras();
            const next = cameras.find((c) => !this.triedDeviceIds.includes(c.deviceId));

            if (!next) {
                this.lensProbeDone = true;
                return;
            }

            console.debug('[scanner] no autofocus on current lens, trying', next.label || next.deviceId);
            // Changes the computed -> the library restarts on the new lens ->
            // onCameraOn runs again and re-evaluates.
            this.activeDeviceId = next.deviceId;
        },

        onCameraError(error) {
            // A persisted deviceId goes stale whenever site data is cleared,
            // camera permission is revoked and re-granted, or the app is opened
            // in another browser -- ids are origin- and permission-scoped. A
            // stale `deviceId: {exact}` fails here, and without this fallback it
            // would leave a permanently black scanner on a phone that used to
            // work. Burn the saved id and retry once on the UA's own choice.
            const staleId = ['OverconstrainedError', 'NotFoundError'].includes(error?.name);
            if (staleId && this.activeDeviceId && !this.deviceIdRetried) {
                console.debug('[scanner] saved camera unavailable, falling back to default');
                this.deviceIdRetried = true;
                clearPreferredDeviceId();
                this.triedDeviceIds = [];
                this.lensProbeDone = true;
                this.activeDeviceId = null;
                return;
            }

            console.error('[scanner] camera error:', error);
            const key = mapCameraError(error);
            this.errorMessage = key
                ? this.$t(key)
                : this.$t('scan.initError') + (error?.message ?? '');
        },

        onDetect(decodedArray) {
            if (this.scanProcessing) return;

            const scanned = (decodedArray?.[0]?.rawValue ?? '').trim();
            if (!scanned || scanned === this.rejectedValue) return;

            // Deliberately NOT the `paused` prop: it sits inside the library's
            // watched cameraSettings, so pausing tears the stream down and
            // resuming does a fresh getUserMedia -- about 1.5s of black preview
            // every time. A plain flag holds off the 40ms detect loop just as
            // well and the camera never stops.
            this.scanProcessing = true;
            this.$emit('decode', scanned);
        },

        /**
         * Carry on scanning after the parent has handled a value it could not
         * use. Passing that value back suppresses it, so the label still sitting
         * in frame does not re-fire, but a different code is picked up at once.
         *
         * @param {string|null} rejectedValue
         */
        resume(rejectedValue = null) {
            this.rejectedValue = rejectedValue;
            this.scanProcessing = false;
        },
    },
};
</script>

<style scoped>
.scanner-modal {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.scanner-content {
    /* Not a hardcoded white: this panel has to follow the colour mode. */
    background: var(--bs-body-bg);
    padding: 20px;
    border-radius: 8px;
    text-align: center;
    max-width: min(480px, 90vw);
}
</style>
